// ===== Group Create + Invite Modal =====
// Phase 6: 그룹방 생성(멤버 멀티셀렉트 + 선택적 제목) + 기존 group 방 멤버 초대 UI.
// 그룹 생성은 기존 POST /api/rooms(roomType='group' + memberActorIds)를 그대로 재사용한다 —
// DM 생성 코드(roster-list.js _openDmWith)를 복제하지 않는다(CAP06 치트 방지).
// 초대는 P5가 채운 POST /api/rooms/:id/members(addMember)를 그대로 소비한다.
// 모달 프레임워크는 profile-edit.js와 동일한 plugin-restart-modal-* CSS를 재사용한다(새 모달 프레임워크 금지).

const GroupCreate = (() => {
  interface Person { actorId: string; displayName?: string; isSelf?: boolean }
  interface RoomResult { id: string; sessionId?: string }
  interface ModalFields { titleInput: HTMLInputElement; checkboxes: HTMLInputElement[] }
  function _isRecord(value: unknown): value is Record<string, unknown> { return !!value && typeof value === "object" && !Array.isArray(value); }
  function _errorMessage(error: unknown) { return error instanceof Error ? error.message : String(error); }
  async function _fetchPeople() {
    const res = await fetch("/api/rooms/actors");
    if (!res.ok) return [];
    const data: unknown = await res.json();
    // roster-list.js와 동일한 "사람만" 필터: system:* 액터(에이전트)는 제외한다.
    // 나 자신은 방 생성 시 owner로 자동 편입되므로(room-store.createRoom) 선택지에서도 제외한다.
    if (!_isRecord(data) || data.ok !== true || !Array.isArray(data.users)) return [];
    return data.users.filter((u): u is Person => _isRecord(u) && typeof u.actorId === "string" && !u.isSelf && !u.actorId.startsWith("system:"));
  }

  async function _fetchExistingMemberIds(roomId: string): Promise<Set<string>> {
    const res = await fetch(`/api/rooms/${roomId}/members`);
    if (!res.ok) return new Set();
    const data: unknown = await res.json();
    if (!_isRecord(data) || data.ok !== true || !Array.isArray(data.members)) return new Set();
    return new Set(data.members.filter(_isRecord).map((m) => m.actorId).filter((id): id is string => typeof id === "string"));
  }

  function _renderChecklist(people: Person[]) {
    const list = document.createElement("div");
    list.style.cssText = "max-height:240px;overflow-y:auto;margin:0 0 12px;";
    const checkboxes: HTMLInputElement[] = [];

    people.forEach((actor) => {
      const row = document.createElement("label");
      row.className = "plugin-restart-modal-check";

      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.value = actor.actorId;

      const name = document.createElement("span");
      name.textContent = actor.displayName || actor.actorId;

      row.appendChild(checkbox);
      row.appendChild(name);
      list.appendChild(row);
      checkboxes.push(checkbox);
    });

    return { list, checkboxes };
  }

  function _selectedActorIds(checkboxes: HTMLInputElement[]) {
    return checkboxes.filter((c) => c.checked).map((c) => c.value);
  }

  function _showError(errorEl: HTMLElement, message: string) {
    errorEl.textContent = message;
    errorEl.style.display = "";
  }

  function _buildModal(title: string) {
    const overlay = document.createElement("div");
    overlay.className = "plugin-restart-modal-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");

    const modal = document.createElement("div");
    modal.className = "plugin-restart-modal";

    const header = document.createElement("div");
    header.className = "plugin-restart-modal-header";
    header.textContent = title;

    const body = document.createElement("div");
    body.className = "plugin-restart-modal-body";

    const errorP = document.createElement("p");
    errorP.className = "plugin-restart-warning";
    errorP.style.display = "none";

    const footer = document.createElement("div");
    footer.className = "plugin-restart-modal-footer";

    const cancelBtn = document.createElement("button");
    cancelBtn.className = "agent-btn agent-btn-cancel";
    cancelBtn.textContent = "취소";

    const confirmBtn = document.createElement("button");
    confirmBtn.className = "agent-btn agent-btn-save";

    footer.appendChild(cancelBtn);
    footer.appendChild(confirmBtn);
    modal.appendChild(header);
    modal.appendChild(body);
    modal.appendChild(footer);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    const overlayId = "group-modal-" + Date.now();
    OverlayRegistry.register(overlayId, close);

    function close() {
      overlay.remove();
      document.removeEventListener("keydown", onEscape);
      OverlayRegistry.unregister(overlayId);
    }
    function onEscape(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    cancelBtn.addEventListener("click", close);
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) close();
    });
    document.addEventListener("keydown", onEscape);

    return { body, errorP, confirmBtn, close };
  }

  // ===== 그룹 생성 =====

  async function _createGroup({ titleInput, checkboxes }: ModalFields): Promise<RoomResult> {
    const memberActorIds = _selectedActorIds(checkboxes);
    if (memberActorIds.length === 0) {
      throw new Error("no_members_selected");
    }

    const title = titleInput.value.trim() || null;
    const res = await fetch("/api/rooms", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ roomType: "group", title, memberActorIds }),
    });
    const data: unknown = await res.json().catch(() => null);
    if (!res.ok || !_isRecord(data) || data.ok !== true || !_isRecord(data.room) || typeof data.room.id !== "string") {
      throw new Error(_isRecord(data) && typeof data.error === "string" ? data.error : "group_create_failed");
    }
    return { id: data.room.id, sessionId: typeof data.room.sessionId === "string" ? data.room.sessionId : undefined };
  }

  async function openCreate() {
    const people = await _fetchPeople();
    const { body, errorP, confirmBtn, close } = _buildModal("새 그룹 만들기");
    confirmBtn.textContent = "만들기";

    if (people.length === 0) {
      const empty = document.createElement("p");
      empty.textContent = "초대할 수 있는 팀원이 없어요";
      body.appendChild(empty);
      return;
    }

    const titleLabel = document.createElement("p");
    titleLabel.textContent = "그룹 이름 (선택)";
    const titleInput = document.createElement("input");
    titleInput.type = "text";
    titleInput.placeholder = "예: 스터디 모임";

    const memberLabel = document.createElement("p");
    memberLabel.textContent = "초대할 멤버";

    const { list, checkboxes } = _renderChecklist(people);

    body.appendChild(titleLabel);
    body.appendChild(titleInput);
    body.appendChild(memberLabel);
    body.appendChild(list);
    body.appendChild(errorP);

    confirmBtn.addEventListener("click", async () => {
      errorP.style.display = "none";
      try {
        const room = await withLoadingFeedback(confirmBtn, () => _createGroup({ titleInput, checkboxes }), {
          loadingText: "만드는 중...",
        });
        if (!room) throw new Error("group_create_failed");
        close();

        if (typeof RoomList !== "undefined") RoomList.refresh();
        if (wsClient && wsClient.isConnected && room.sessionId) {
          wsClient.resumeSession(room.sessionId, { roomId: room.id });
        }
      } catch (err) {
        console.error("[group-create] 생성 실패:", err);
        const message = _errorMessage(err) === "no_members_selected"
          ? "멤버를 한 명 이상 선택해 주세요."
          : "그룹 생성에 실패했어요. 다시 시도해 주세요.";
        _showError(errorP, message);
      }
    });
  }

  // ===== 멤버 초대(기존 group 방) =====

  async function _inviteMembers(roomId: string, checkboxes: HTMLInputElement[]) {
    const inviteeActorIds = _selectedActorIds(checkboxes);
    if (inviteeActorIds.length === 0) {
      throw new Error("no_members_selected");
    }

    for (const inviteeActorId of inviteeActorIds) {
      const res = await fetch(`/api/rooms/${roomId}/members`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ inviteeActorId }),
      });
      const data: unknown = await res.json().catch(() => null);
      if (!res.ok || !_isRecord(data) || data.ok !== true) {
        throw new Error(_isRecord(data) && typeof data.error === "string" ? data.error : "invite_failed");
      }
    }
  }

  async function openInvite(roomId: string) {
    const [people, existingMemberIds] = await Promise.all([
      _fetchPeople(),
      _fetchExistingMemberIds(roomId),
    ]);
    const invitable = people.filter((actor) => !existingMemberIds.has(actor.actorId));

    const { body, errorP, confirmBtn, close } = _buildModal("멤버 초대");
    confirmBtn.textContent = "초대";

    if (invitable.length === 0) {
      const empty = document.createElement("p");
      empty.textContent = "초대할 수 있는 팀원이 없어요";
      body.appendChild(empty);
      return;
    }

    const { list, checkboxes } = _renderChecklist(invitable);
    body.appendChild(list);
    body.appendChild(errorP);

    confirmBtn.addEventListener("click", async () => {
      errorP.style.display = "none";
      try {
        await withLoadingFeedback(confirmBtn, () => _inviteMembers(roomId, checkboxes), {
          loadingText: "초대하는 중...",
        });
        close();
        if (typeof RoomList !== "undefined") RoomList.refresh();
        // 헤더 바(제목 라벨·멤버 칩·인원 카운트)도 새 멤버십으로 재바인딩한다.
        // dm → group 승격 시 방 목록 배지·초대 노출은 위 RoomList.refresh()가 갱신한다.
        if (typeof bindRoomHeader === "function") bindRoomHeader(roomId);
      } catch (err) {
        console.error("[group-create] 초대 실패:", err);
        const message = _errorMessage(err) === "no_members_selected"
          ? "초대할 멤버를 한 명 이상 선택해 주세요."
          : "초대에 실패했어요. 다시 시도해 주세요.";
        _showError(errorP, message);
      }
    });
  }

  // ===== 방 이름 변경(컨텍스트 메뉴 진입) =====

  async function _renameRoom(roomId: string, title: string) {
    const res = await fetch(`/api/rooms/${roomId}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ title }),
    });
    const data: unknown = await res.json().catch(() => null);
    if (!res.ok || !_isRecord(data) || data.ok !== true) {
      throw new Error(_isRecord(data) && typeof data.error === "string" ? data.error : "rename_failed");
    }
    return data.room;
  }

  function openRename(roomId: string, currentTitle?: string) {
    const { body, errorP, confirmBtn, close } = _buildModal("방 이름 변경");
    confirmBtn.textContent = "변경";

    const titleInput = document.createElement("input");
    titleInput.type = "text";
    titleInput.value = currentTitle || "";
    titleInput.placeholder = "방 이름";
    body.appendChild(titleInput);
    body.appendChild(errorP);
    titleInput.focus();
    titleInput.select();

    confirmBtn.addEventListener("click", async () => {
      errorP.style.display = "none";
      const title = titleInput.value.trim();
      if (!title) {
        _showError(errorP, "방 이름을 입력해 주세요.");
        return;
      }
      try {
        await withLoadingFeedback(confirmBtn, () => _renameRoom(roomId, title), {
          loadingText: "변경하는 중...",
        });
        close();
        if (typeof RoomList !== "undefined") RoomList.refresh();
        // 지금 보고 있는 방이면 헤더 라벨도 즉시 재바인딩.
        if (typeof bindRoomHeader === "function") bindRoomHeader(roomId);
      } catch (err) {
        console.error("[group-create] 이름 변경 실패:", err);
        _showError(errorP, "이름 변경에 실패했어요. 다시 시도해 주세요.");
      }
    });
  }

  return { openCreate, openInvite, openRename };
})();

document.addEventListener("DOMContentLoaded", () => {
  const newGroupBtn = document.getElementById("newGroupBtn");
  if (newGroupBtn) {
    newGroupBtn.addEventListener("click", () => GroupCreate.openCreate());
  }

  const groupInviteBtn = document.getElementById("groupInviteBtn");
  if (groupInviteBtn) {
    groupInviteBtn.addEventListener("click", () => {
      const roomId = groupInviteBtn.dataset.roomId;
      if (roomId) GroupCreate.openInvite(roomId);
    });
  }
});
